if (!customElements.get("flavor-picker")) {
  customElements.define(
    "flavor-picker",
    class FlavorPicker extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this.onFlavorClick.bind(this));
      }

      disconnectedCallback() {
        this._abortController?.abort();
      }

      onFlavorClick(event) {
        const btn = event.target.closest("[data-product-url]");
        if (!btn) return;
        if (btn.getAttribute("aria-current") === "true") return;

        this.selectFlavor(btn);
      }

      selectFlavor(btn) {
        // Optimistic visual update — immediate feedback
        this.querySelectorAll("[data-product-url]").forEach((b) => {
          b.setAttribute("aria-current", "false");
          b.classList.remove("flavor-pill--selected");
        });
        btn.setAttribute("aria-current", "true");
        btn.classList.add("flavor-pill--selected");

        // Update the legend selected-value label right away
        const selectedLabel = this.querySelector(".flavor-picker__selected-value");
        if (selectedLabel) {
          selectedLabel.textContent = btn.dataset.flavorLabel || btn.querySelector(".flavor-pill__label")?.textContent || "";
        }

        this.fetchProduct(btn.dataset.productUrl);
      }

      fetchProduct(productUrl) {
        this._abortController?.abort();
        this._abortController = new AbortController();

        const sectionId = this.dataset.section;
        // Must be the enclosing product-info (PDP or quick-add modal). A global
        // querySelector would always hit the first MainProduct-* on the page — usually
        // the main PDP — so flavor changes inside "Frequently bought together" modals
        // never updated the modal form and the wrong variant was added to cart.
        const productInfo = this.closest("product-info");

        if (!productInfo) {
          console.warn("[flavor-picker] Missing parent product-info");
          return;
        }

        productInfo.setAttribute("aria-busy", "true");

        // Quick-add modal rewrites section ids with a "quickadd-" prefix; Shopify's
        // ?section_id= must use the real section id (stored on product-info by quick-add.js).
        const rawSectionId =
          productInfo.dataset.originalSection ||
          productInfo.dataset.section ||
          this.dataset.section;
        const sectionIdForFetch =
          rawSectionId && String(rawSectionId).startsWith("quickadd-")
            ? String(rawSectionId).slice("quickadd-".length)
            : rawSectionId;

        // ── Clone the picker so we can re-inject it after the DOM swap ──────
        // The swap may target a product without flavor_picker in its template,
        // so we always preserve our element explicitly.
        const pickerClone = this.cloneNode(true);

        // Sync selected state on the clone to the NEW product
        pickerClone.querySelectorAll("[data-product-url]").forEach((b) => {
          const isSelected = b.dataset.productUrl === productUrl;
          b.setAttribute("aria-current", String(isSelected));
          b.classList.toggle("flavor-pill--selected", isSelected);
        });
        const cloneLabel = pickerClone.querySelector(".flavor-picker__selected-value");
        const cloneActive = pickerClone.querySelector('[aria-current="true"]');
        if (cloneLabel && cloneActive) {
          cloneLabel.textContent = cloneActive.dataset.flavorLabel || cloneActive.querySelector(".flavor-pill__label")?.textContent || "";
        }

        // ── Section Rendering API: only fetch the product section ────────────
        // This is faster than a full page fetch and avoids re-rendering
        // unrelated sections (FAQ, ticker, etc.).
        fetch(`${productUrl}?section_id=${encodeURIComponent(sectionIdForFetch)}`, {
          signal: this._abortController.signal,
        })
          .then((response) => response.text())
          .then((responseText) => {
            productInfo.removeAttribute("aria-busy");

            const html = new DOMParser().parseFromString(responseText, "text/html");

            const newProductInfo = html.querySelector('product-info[id^="MainProduct"]');
            if (!newProductInfo) return;

            // ── Do NOT change window.location to the new product URL ─────────
            // The page was loaded via product.set.json — changing the URL to
            // another product handle would make a browser refresh serve
            // product.json (the normal template) instead. We keep the URL intact
            // and only update product-info's internal data-url attribute so that
            // subsequent variant selections work correctly against the new product.

            // Cancel scroll-trigger animations inside the incoming content
            newProductInfo.querySelectorAll(".scroll-trigger").forEach((el) => el.classList.add("scroll-trigger--cancel"));

            // ── Swap the section content ─────────────────────────────────────
            const doSwap = () => {
              productInfo.innerHTML = newProductInfo.innerHTML;

              // Keep product-info data attributes in sync so variant selection
              // continues to work correctly on the new product
              productInfo.dataset.url = productUrl;
              // Do not rewrite the browser URL when editing inside quick-add — user is
              // still on the host PDP (e.g. product.set).
              if (!productInfo.closest("quick-add-modal")) {
                history.pushState({}, "", productUrl);
              }

              // ── Re-inject the flavor picker ──────────────────────────────
              // If the new product's template also has a flavor-picker, replace
              // it with our clone (correct selected state). Otherwise insert it
              // after the price block so it always stays visible.
              const rendered = productInfo.querySelector("flavor-picker");
              if (rendered) {
                rendered.replaceWith(pickerClone);
              } else {
                const anchor =
                  productInfo.querySelector(".product-price-block") ||
                  productInfo.querySelector(".product-variant-picker-block") ||
                  productInfo.querySelector(".product__info-container");

                if (anchor && anchor !== productInfo.querySelector(".product__info-container")) {
                  anchor.after(pickerClone);
                } else {
                  // last resort: prepend inside info container
                  productInfo.querySelector(".product__info-container")?.prepend(pickerClone);
                }
              }

              // Re-init third-party / Shopify integrations
              window?.Shopify?.PaymentButton?.init();
              window?.ProductModel?.loadShopifyXR();

              // Skio: inline app-block scripts do NOT run after innerHTML swap. Re-run globals
              // so plan picker binds + Subscribe UI (incl. frequency dropdown) works.
              try {
                if (typeof window.SkioPlanPickerAutoInit === "function") {
                  window.SkioPlanPickerAutoInit();
                }
                if (typeof window.SkioLoadJSAddListeners === "function") {
                  window.SkioLoadJSAddListeners();
                }
              } catch (e) {
                /* noop */
              }
              document.dispatchEvent(
                new CustomEvent("lasso:product-info-updated", {
                  bubbles: true,
                  detail: { productUrl },
                })
              );
            };

            if (document.startViewTransition) {
              document.startViewTransition(doSwap);
            } else {
              doSwap();
            }
          })
          .catch((error) => {
            productInfo.removeAttribute("aria-busy");
            if (error.name !== "AbortError") console.error("[flavor-picker]", error);
          });
      }
    }
  );
}
